import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

const AuthCallback = () => {
  const { exchangeSession, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [errorDetails, setErrorDetails] = useState('');

  useEffect(() => {
    // Prevent double execution in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      // Try to get session_id from multiple sources
      let sessionId = null;
      
      // 1. Check URL hash (e.g., #session_id=xxx)
      const hash = window.location.hash;
      const hashMatch = hash.match(/session_id=([^&]+)/);
      if (hashMatch) {
        sessionId = hashMatch[1];
      }
      
      // 2. Check query parameters (e.g., ?session_id=xxx)
      if (!sessionId) {
        sessionId = searchParams.get('session_id');
      }
      
      // 3. Check if it's in the full URL somewhere
      if (!sessionId) {
        const fullUrl = window.location.href;
        const urlMatch = fullUrl.match(/session_id=([^&\s#]+)/);
        if (urlMatch) {
          sessionId = urlMatch[1];
        }
      }
      
      console.log('AuthCallback - Session ID search:', {
        hash: window.location.hash,
        search: window.location.search,
        href: window.location.href,
        foundSessionId: sessionId ? 'yes' : 'no',
        origin: window.location.origin
      });
      
      if (!sessionId) {
        console.error('No session_id found in URL');
        setErrorDetails('No session ID found in the redirect URL. Please try again.');
        setStatus('error');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        setStatus('exchanging');
        const userData = await exchangeSession(sessionId);
        
        // Clear the URL parameters
        window.history.replaceState(null, '', '/auth/callback');
        
        setStatus('success');
        // Navigate after state is propagated
        // Check if user needs onboarding (new users)
        setTimeout(() => {
          if (userData && !userData.onboarding_completed) {
            navigate('/onboarding', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        }, 300);
      } catch (error) {
        console.error('Session exchange failed:', error);
        setErrorDetails(error.message || 'Failed to verify your account. Please try logging in again.');
        setStatus('error');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processSession();
  }, [exchangeSession, navigate, searchParams]);

  // If user is already set (from a previous session), redirect immediately
  useEffect(() => {
    if (user && status === 'success') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, status, navigate]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center max-w-md px-6">
        {status === 'error' ? (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-red-500 text-2xl">!</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Authentication Failed</h2>
            <p className="text-muted-foreground mb-4">{errorDetails}</p>
            <p className="text-muted-foreground text-sm">Redirecting to login...</p>
          </>
        ) : (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h2 className="text-xl font-bold text-white mb-2">
              {status === 'exchanging' ? 'Verifying your account...' : 'Signing you in...'}
            </h2>
            <p className="text-muted-foreground">Please wait while we complete authentication</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
